import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import isEmail from "validator/lib/isEmail";
import isMobilePhone from "validator/lib/isMobilePhone";
import COUNTRIES, {
  getCountryByCode,
  isStateMandatory,
} from "../../helpers/countries";
import { ROUTES } from "../../helpers/routes";
import { getCountryStates } from "../../helpers/states";
import { rem, toREM, vwDesktop, vwMobile } from "../../helpers/styles";
import { MEDIA_DESKTOP } from "../../helpers/theme";
import BackButton from "../shared/BackButton";
import Button, { ButtonType } from "../shared/Button";
import Input from "../shared/Input/Input";
import TextArea from "../shared/TextArea/TextArea";
import Select from "../StartSelling/Select";
import { ZendeskService } from "../../services/zendesk";
import { FormstackService } from "../../services/formstack";
import ReCAPTCHA from "react-google-recaptcha";
import { useMessages } from "../../hooks/useMessages";
import { pushGtmDataLayer } from "../../helpers/analytics";

export type ProductInquiriesFormProps = {
  backLabel?: string;
  productInquiries?: {
    button: string;
    cityPlaceholder: string;
    statePlaceholder: string;
    countryPlaceholder: string;
    emailPlaceholder: string;
    klaviyoId: string;
    storePlaceholder: string;
    lotCodePlaceholder: string;
    orderNumberPlaceholder: string;
    subjectLinePlaceholder: string;
    successMessage: string;
    title: string;
    yourMessagePlaceholder: string;
    firstNamePlaceholder: string;
    lastNamePlaceholder: string;
    yourPhonePlaceholder: string;
  };
};

const FORM_INITIAL_STATE = {
  productInquiries: {
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    city: "",
    state: "",
    country: "",
    store: "",
    lotCode: "",
    orderNumber: "",
    subject: "",
    message: "",
  },
};

const ProductInquiriesForm = ({
  backLabel,
  productInquiries,
}: ProductInquiriesFormProps): React.ReactElement => {
  const recaptchaRef = React.useRef();
  const [form, setForm] = useState(FORM_INITIAL_STATE.productInquiries);
  const [success, setSuccess] = useState<boolean>(false);
  const [errorMessages, setErrorMessages] = useState(
    FORM_INITIAL_STATE.productInquiries
  );
  const messages = useMessages();

  const handleChange = (event: React.FormEvent) => {
    const { name, value } = event.target as HTMLInputElement;
    const newForm = { ...form };
    newForm[name] = value;
    setForm(newForm);
  };

  const isFormValid = () => {
    setErrorMessages({
      firstName: !form.firstName ? messages.fieldRequired : "",
      lastName: !form.lastName ? messages.fieldRequired : "",
      email: !form.email
        ? messages.fieldRequired
        : !isEmail(form?.email)
        ? messages.validEmail
        : "",
      phoneNumber:
        form?.phoneNumber && !isMobilePhone(form?.phoneNumber)
          ? messages.validPhoneNumber
          : "",
      country: !form.country ? messages.fieldRequired : "",
      state:
        isStateMandatory(form?.country) && !form.state
          ? messages.fieldRequired
          : "",
      city: "",
      store: "",
      lotCode: "",
      orderNumber: "",
      subject: !form.subject ? messages.fieldRequired : "",
      message: !form.message ? messages.fieldRequired : "",
    });

    return (
      form?.firstName &&
      form?.lastName &&
      isEmail(form?.email) &&
      (form?.phoneNumber ? isMobilePhone(form?.phoneNumber) : true) &&
      form?.country &&
      (isStateMandatory(form?.country) ? form?.state : true) &&
      form?.subject &&
      form?.message
    );
  };

  const handleSubmit = useCallback(
    async (event: React.MouseEvent) => {
      event.preventDefault();

      if (!isFormValid()) return;

      try {
        await recaptchaRef.current?.executeAsync();
        if (productInquiries?.klaviyoId){
          await ZendeskService.createProductInquiry({
            ...form,
            country: {
              code: form?.country,
              name: getCountryByCode(form?.country)?.Name,
            },
          });
        }
        await FormstackService.productInquiry({
          ...form,
          country: {
            code: form?.country,
            name: getCountryByCode(form?.country)?.Name,
          },
        });
        const tId = setTimeout(() => {
          setSuccess(false);
          setForm(FORM_INITIAL_STATE.productInquiries);
          clearTimeout(tId);
        }, 10000);
        setSuccess(true);

        pushGtmDataLayer({
          event: "submit_an_inquiry_finish",
          inquiry_type: productInquiries?.title,
        });
      } catch (error) {
        console.log(error)
      }
    },
    [form]
  );

  const states = useMemo(() => getCountryStates(form.country), [form.country]);

  // reset state after country changed
  useEffect(() => {
    setForm((prev) => ({ ...prev, state: "" }));
  }, [form.country]);

  return (
    <FormContainer>
      <BackButton label={backLabel} link={ROUTES.CONTACT_US} />

      {success ? (
        <SuccessMessage>{productInquiries?.successMessage}</SuccessMessage>
      ) : (
        <FieldsWrapper>
          <Title>{productInquiries?.title}</Title>
          {productInquiries && (
            <Fields>
              <Input
                label={productInquiries?.firstNamePlaceholder}
                name="firstName"
                value={form?.firstName}
                onChange={handleChange}
                error={errorMessages.firstName}
              />
              <Input
                label={productInquiries?.lastNamePlaceholder}
                name="lastName"
                value={form?.lastName}
                onChange={handleChange}
                error={errorMessages.lastName}
              />

              <Input
                label={productInquiries?.emailPlaceholder}
                name="email"
                onChange={handleChange}
                value={form?.email}
                error={errorMessages.email}
              />
              <Input
                label={productInquiries?.yourPhonePlaceholder}
                name="phoneNumber"
                onChange={handleChange}
                value={form?.phoneNumber}
                error={errorMessages.phoneNumber}
              />
              <Select
                aria-label={productInquiries?.countryPlaceholder}
                placeHolder={productInquiries?.countryPlaceholder}
                name="country"
                value={form?.country}
                onChange={handleChange}
                options={COUNTRIES}
                labelField="Name"
                valueField="Code"
                error={errorMessages.country}
              />
              {states ? (
                <Select
                  aria-label={productInquiries?.statePlaceholder}
                  placeHolder={productInquiries?.statePlaceholder}
                  name="state"
                  value={form.state}
                  onChange={handleChange}
                  options={states}
                  labelField="Name"
                  valueField="Code"
                  error={errorMessages.state}
                />
              ) : (
                <Input
                  label={productInquiries?.statePlaceholder}
                  onChange={handleChange}
                  name="state"
                  value={form?.state}
                  error={errorMessages.state}
                />
              )}
              <Input
                label={productInquiries?.cityPlaceholder}
                onChange={handleChange}
                name="city"
                value={form?.city}
                error={errorMessages.city}
              />
              <Input
                label={productInquiries?.storePlaceholder}
                onChange={handleChange}
                name="store"
                value={form?.store}
                error={errorMessages.store}
              />
              <Input
                label={productInquiries?.orderNumberPlaceholder}
                onChange={handleChange}
                name="orderNumber"
                value={form?.orderNumber}
                error={errorMessages.orderNumber}
              />
              <Input
                label={productInquiries?.lotCodePlaceholder}
                onChange={handleChange}
                name="lotCode"
                value={form?.lotCode}
                error={errorMessages.lotCode}
              />
              <Input
                label={productInquiries?.subjectLinePlaceholder}
                onChange={handleChange}
                name="subject"
                value={form?.subject}
                error={errorMessages.subject}
              />
              <TextArea
                label={productInquiries?.yourMessagePlaceholder}
                name="message"
                onChange={handleChange}
                value={form?.message}
                error={errorMessages.message}
              />
            </Fields>
          )}
          <FormAction>
            <Button
              onClick={handleSubmit}
              type={ButtonType.PRIMARY_LIGHT}
              label={productInquiries?.button || ""}
              submit
            />
          </FormAction>
        </FieldsWrapper>
      )}
      <ReCAPTCHA
        ref={recaptchaRef}
        size="invisible"
        sitekey={process.env.GATSBY_RECAPTCHA_KEY}
      />
    </FormContainer>
  );
};

export default ProductInquiriesForm;

const FormAction = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${vwMobile(20)};
  margin-top: ${vwMobile(40)};

  ${MEDIA_DESKTOP} {
    justify-content: flex-end;
    gap: ${vwDesktop(20)};
    margin-top: ${vwDesktop(40)};
  }

  > * {
    flex: 1;
    ${MEDIA_DESKTOP} {
      flex: 0 0 auto;
      width: ${vwDesktop(180)};
    }
  }
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${vwMobile(16)};

  ${MEDIA_DESKTOP} {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: ${vwDesktop(16)};

    & > :nth-child(9),
    & > :nth-child(10),
    & > :nth-child(11),
    & > :last-child {
      grid-column: 1 / 3;
    }
  }
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${vwMobile(30)};
  color: var(--color-white);

  background-color: var(--color-green-100);
  padding: ${vwMobile(40)} ${vwMobile(20)} ${vwMobile(36)};

  ${MEDIA_DESKTOP} {
    gap: ${vwDesktop(30)};
    width: 100%;
    padding: ${vwDesktop(40)} ${vwDesktop(100)} ${vwDesktop(80)};
  }

  h3 {
    font-size: ${rem(36)};
    font-weight: 900;
    text-transform: uppercase;
    margin-bottom: ${vwMobile(15)};
    letter-spacing: 0.03em;

    ${MEDIA_DESKTOP} {
      margin-bottom: ${vwDesktop(15)};
    }
  }
`;

const FieldsWrapper = styled.div`
  ${MEDIA_DESKTOP} {
    padding: 0 ${vwDesktop(105)};
  }
`;

const Title = styled.h1`
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  font-size: ${rem(44)};
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.012em;
  line-height: ${rem(53)};
  margin-bottom: ${vwMobile(40)};

  ${MEDIA_DESKTOP} {
    margin-bottom: ${vwDesktop(40)};
  }
`;

const SuccessMessage = styled.p`
  width: 100%;
  text-align: center;
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  font-size: ${toREM(18)}rem;
  font-weight: 800;
  letter-spacing: 0.08em;
`;
